import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { handleGetRequest } from '../../services/GetTemplate';
import { baseURL } from '../../Config';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import { useFormik } from 'formik';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { handlePostRequest } from '../../services/PostTemplate';
import { toast } from 'react-toastify';

function AddNewZone({ initialPolygon = null, initialData = null, onClose = null }) {
    const [zoneName, setZoneName] = useState('');
    const [zoneCode, setZoneCode] = useState('');
    const [description, setDescription] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [sector, setSector] = useState('');
    // const [radiusMeters, setRadiusMeters] = useState(null); // meters
    // const [totalCapacity, setTotalCapacity] = useState(null);
    const [userId, setUserId] = useState(localStorage.getItem('userId') || null);
    const [zoneImage, setZoneImage] = useState('');
    const [zoneImageFile, setZoneImageFile] = useState(null);
    const [zoneImagePreview, setZoneImagePreview] = useState('');
    const [peakHours, setPeakHours] = useState([]);
    const [vehicleRates, setVehicleRates] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [showRatesDialog, setShowRatesDialog] = useState(false);
    const [showPeakHoursDialog, setShowPeakHoursDialog] = useState(false);
    const [position, setPosition] = useState(null);
    const mapElRef = useRef(null); // DOM element for map
    const mapRef = useRef(null); // Leaflet map instance
    const markerRef = useRef(null); // marker layer
    const drawnItemsRef = useRef(null); // featureGroup for drawings
    const drawerRef = useRef(null); // current drawer instance
    const fileInputRef = useRef(null);
    const [polygonGeoJSON, setPolygonGeoJSON] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const isDrawingRef = useRef(false);
    const devicePositionRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);
    const [vehicleRatesError, setVehicleRatesError] = useState('');

    const dispatch = useDispatch();

    // when initialData prop changes (editing an existing record), prefill fields
    useEffect(() => {
        if (!initialData) return;
        try {
            setUserId(localStorage.getItem('userId') || null);
            // Set formik field for validation
            if (initialData.zone && initialData.zone.zoneImage) {
                formik.setFieldValue('zoneImage', initialData.zone.zoneImage);
                setZoneImagePreview(`${baseURL.replace('/api/', '/')}${initialData.zone.zoneImage}`);
            }
            // if the initialData contains a polygon and initialPolygon wasn't provided separately,
            // it will be handled by the map initialization code that reads `initialPolygon` prop.

            // Update formik values for editing
            formik.setValues({
                zoneName: initialData.zone.zoneName || '',
                zoneCode: initialData.zone.zoneCode || initialData.zoneId || '',
                description: initialData.zone.description || initialData.zoneDesc || '',
                address: initialData.zone.address || '',
                city: 'Islamabad', // Keep hardcoded for now
                // sector: initialData.zone.sector || '',
                // radiusMeters: initialData.zone.radiusMeters != null ? initialData.zone.radiusMeters : initialData.radius != null ? initialData.radius : '',
                // totalCapacity: initialData.zone.totalCapacity != null ? initialData.zone.totalCapacity : '',
                zoneImage: initialData.zone.zoneImage || ''
            });

            // Set peak hours and vehicle rates (robust parsing for multiple backend shapes)
            if (initialData.peakHours && Array.isArray(initialData.peakHours)) {
                setPeakHours(
                    initialData.peakHours.map((ph) => {
                        const rawStart = ph.startTime || ph.StartTime || '';
                        const rawEnd = ph.endTime || ph.EndTime || '';
                        // input[type=time] expects HH:MM — backend may provide HH:MM:SS
                        const start = rawStart && rawStart.length >= 5 ? String(rawStart).substring(0, 5) : rawStart;
                        const end = rawEnd && rawEnd.length >= 5 ? String(rawEnd).substring(0, 5) : rawEnd;
                        // multiplier may be provided as `multiplier` number or embedded in `description` like '2x'
                        let mult = null;
                        if (ph.multiplier != null && ph.multiplier !== '') mult = parseInt(ph.multiplier, 10);
                        else if (ph.description) mult = parseInt(String(ph.description).replace(/x/gi, ''), 10);
                        if (isNaN(mult) || mult == null) mult = 1;
                        return { startTime: start || '', endTime: end || '', multiplier: mult };
                    })
                );
                if (initialData.peakHours.length > 0) {
                    setShowPeakHoursDialog(true);
                }
            }
            if (initialData.vehicleRates && Array.isArray(initialData.vehicleRates)) {
                setVehicleRates(
                    initialData.vehicleRates.map((vr) => ({
                        vehicleTypeID: vr.vehicleTypeID ?? vr.VehicleTypeID ?? vr.vehicleTypeId ?? vr.vehicleId,
                        ratePerHour: vr.ratePerHour != null && vr.ratePerHour !== '' ? Number(vr.ratePerHour) : 0
                    }))
                );
                if (initialData.vehicleRates.length > 0) {
                    // open rates panel deterministically (avoid relying on toggle closure)
                    setShowRatesDialog(true);
                }
            }
        } catch (err) {
            // toast.warn('Failed to prefill zone data. Please try again.');
        }
    }, [initialData]);

    // fetch vehicle types for display (names)
    useEffect(() => {
        let mounted = true;
        const loadVehicleTypes = async () => {
            try {
                // call the backend vehicle types endpoint
                const res = await handleGetRequest('Vehicle/GetAllVehicleType', false);
                let list = [];
                if (Array.isArray(res)) list = res;
                else if (res?.success && Array.isArray(res.data)) list = res.data;
                else if (Array.isArray(res?.data)) list = res.data;
                else if (Array.isArray(res?.items)) list = res.items;

                // normalize to { id, name }
                const normalized = (list || []).map((v) => ({
                    id: v.vehicleId ?? v.VehicleTypeID ?? v.vehicleTypeID ?? v.id ?? v.vehicleTypeId ?? null,
                    name: v.typeName ?? v.VehicleTypeName ?? v.vehicleTypeName ?? v.name ?? v.label ?? String(v)
                }));
                if (mounted) setVehicleTypes(normalized.filter((x) => x.id != null));
            } catch (err) {
                toast.warn('Failed to load vehicle types. Please refresh the page.');
            }
        };
        loadVehicleTypes();
        return () => {
            mounted = false;
        };
    }, []);

    // helper to add or move the marker using refs so it's callable from handlers
    function addOrMoveMarker(latlng) {
        setPosition([latlng.lat, latlng.lng]);
        if (markerRef.current) {
            markerRef.current.setLatLng(latlng);
        } else if (mapRef.current) {
            markerRef.current = L.circleMarker(latlng, { radius: 8, color: 'red' }).addTo(mapRef.current);
        }
    }

    // update or add a rate for a specific vehicle type
    const handleRateChange = (vehicleTypeID, rawValue) => {
        const val = rawValue === '' || rawValue == null ? '' : parseFloat(rawValue);
        setVehicleRates((prev) => {
            const copy = Array.isArray(prev) ? [...prev] : [];
            const idx = copy.findIndex((r) => String(r.vehicleTypeID) === String(vehicleTypeID));
            if (idx >= 0) {
                copy[idx] = { ...copy[idx], ratePerHour: val === '' ? 0 : val };
            } else {
                copy.push({ vehicleTypeID, ratePerHour: val === '' ? 0 : val });
            }
            return copy;
        });
    };

    const openRatesDialog = () => {
        // Toggle the inline rates panel.
        setShowRatesDialog(!showRatesDialog);
    };

    const openPeakHoursDialog = () => {
        if (!showPeakHoursDialog) {
            // Opening the dialog
            if (peakHours.length === 0) {
                setPeakHours([{ startTime: '', endTime: '', multiplier: 1 }]);
            }
        }
        setShowPeakHoursDialog(!showPeakHoursDialog);
    };

    const handleTempRateChange = (vtId, value) => {
        // allow only digits (no decimals) — strip any non-digit characters
        const digits = value === null || value === undefined ? '' : String(value).replace(/\D+/g, '');
        // Update vehicleRates directly
        setVehicleRates((prev) => {
            const copy = Array.isArray(prev) ? [...prev] : [];
            const idx = copy.findIndex((r) => String(r.vehicleTypeID) === String(vtId));
            const rate = digits === '' ? 0 : parseInt(digits, 10);
            if (idx >= 0) {
                copy[idx] = { ...copy[idx], ratePerHour: rate };
            } else {
                copy.push({ vehicleTypeID: vtId, ratePerHour: rate });
            }
            return copy;
        });
    };

    const saveRatesFromDialog = () => {
        setShowRatesDialog(false);
    };

    useEffect(() => {
        if (!mapElRef.current) return;

        // create map and set default view to Islamabad coords (33.6996 N, 73.0362 E)
        const map = L.map(mapElRef.current);
        // default initial view (Islamabad)
        map.setView([33.6996, 73.0362], 13);
        mapRef.current = map;

        // Base layers: OpenStreetMap (default), Esri World Imagery (satellite), Stamen Terrain
        const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors'
        }).addTo(map);

        const esriSat = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
            attribution: 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community'
        });

        const stamenTerrain = L.tileLayer('https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}.jpg', {
            attribution: 'Map tiles by Stamen Design'
        });

        const baseMaps = {
            OpenStreetMap: osmLayer,
            'Satellite (Esri)': esriSat,
            'Terrain (Stamen)': stamenTerrain
        };

        L.control.layers(baseMaps, null, { position: 'topright' }).addTo(map);

        function onMapClick(e) {
            // ignore click-to-place when drawing a polygon
            if (isDrawingRef.current) return;
            addOrMoveMarker(e.latlng);
        }

        map.on('click', onMapClick);

        // add an in-map Locate control (like Google Maps) in top-right
        const LocateControl = L.Control.extend({
            onAdd: function (map) {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const link = L.DomUtil.create('a', '', container);
                link.href = '#';
                link.title = 'Locate Me';
                link.setAttribute('aria-label', 'Locate Me');
                link.style.width = '34px';
                link.style.height = '34px';
                link.style.display = 'flex';
                link.style.alignItems = 'center';
                link.style.justifyContent = 'center';
                link.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 8a4 4 0 100 8 4 4 0 000-8zm0-6v2.1a8 8 0 010 15.8V22a10 10 0 100-20z"/></svg>';

                L.DomEvent.disableClickPropagation(container);
                L.DomEvent.on(link, 'click', L.DomEvent.stop);
                L.DomEvent.on(link, 'click', function (ev) {
                    ev.preventDefault();
                    // perform locate
                    if (!navigator.geolocation) {
                        alert('Geolocation not supported by this browser.');
                        return;
                    }
                    navigator.geolocation.getCurrentPosition(
                        (pos) => {
                            const lat = pos.coords.latitude;
                            const lng = pos.coords.longitude;
                            const latlng = L.latLng(lat, lng);
                            // remember device location so marker can be restored after drawing
                            devicePositionRef.current = latlng;
                            map.setView(latlng, 15);
                            addOrMoveMarker(latlng);
                        },
                        (err) => {
                            toast.warn('Unable to get your location: ' + err.message);
                        },
                        { enableHighAccuracy: true, timeout: 10000 }
                    );
                });

                return container;
            }
        });

        map.addControl(new LocateControl({ position: 'topright' }));

        // feature group to store drawn items
        const drawnItems = L.featureGroup().addTo(map);
        drawnItemsRef.current = drawnItems;

        // If an initial polygon was passed (editing an existing record), load it onto the map.
        // Supported formats:
        // - GeoJSON Feature: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [...] } }
        // - GeoJSON Geometry: { type: 'Polygon', coordinates: [...] }
        // - Plain ring coordinates array: [ [lng, lat], [lng, lat], ... ] (single ring)
        // - Nested coordinates array: [ [ [lng,lat], ... ] ]
        // Support initialPolygon prop OR a boundaryGeoJSON string on initialData
        let initialPolyToLoad = initialPolygon;
        if (!initialPolyToLoad && initialData && initialData.zone && initialData.zone.boundaryGeoJSON) {
            try {
                const parsed = typeof initialData.zone.boundaryGeoJSON === 'string' ? JSON.parse(initialData.zone.boundaryGeoJSON) : initialData.zone.boundaryGeoJSON;
                initialPolyToLoad = parsed;
            } catch (err) {
                toast.warn('Failed to load zone boundary data.');
            }
        }

        if (initialPolyToLoad) {
            try {
                let coords = null;

                // If it's a GeoJSON Feature
                if (initialPolygon.type === 'Feature' && initialPolygon.geometry && initialPolygon.geometry.type === 'Polygon') {
                    coords = initialPolygon.geometry.coordinates;
                } else if (initialPolygon.type === 'Polygon' && initialPolygon.coordinates) {
                    coords = initialPolygon.coordinates;
                } else if (Array.isArray(initialPolygon)) {
                    // if first element is a number pair, we assume it's a single ring and wrap it
                    if (initialPolygon.length > 0 && Array.isArray(initialPolygon[0]) && typeof initialPolygon[0][0] === 'number') {
                        // wrap to make it a polygon's coordinates (array of rings)
                        coords = [initialPolygon];
                    } else {
                        // assume it's already nested (array of rings)
                        coords = initialPolygon;
                    }
                }

                if (coords) {
                    // Calculate center of the polygon
                    const ring = coords[0];
                    if (ring && ring.length > 0) {
                        let totalLng = 0,
                            totalLat = 0;
                        ring.forEach(([lng, lat]) => {
                            totalLng += lng;
                            totalLat += lat;
                        });
                        const centerLng = totalLng / ring.length;
                        const centerLat = totalLat / ring.length;
                        map.setView([centerLat, centerLng], 15);
                    }

                    const feature = { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: coords } };
                    const layer = L.geoJSON(feature).getLayers()[0];
                    if (layer) {
                        drawnItems.clearLayers();
                        drawnItems.addLayer(layer);
                        setPolygonGeoJSON(feature);
                        // fit map to the polygon bounds for visibility
                        try {
                            map.fitBounds(layer.getBounds());
                        } catch (err) {}

                        // (no disabling of any draw control here) keep draw controls available so user can replace polygon
                    }
                }
            } catch (err) {
                toast.warn('Failed to load zone boundary on map.');
            }
        }

        // inject small CSS for drawing vertex points so they appear small
        const styleEl = document.createElement('style');
        styleEl.innerHTML = `
            .leaflet-div-icon.leaflet-editing-icon {
                width: 8px !important;
                height: 8px !important;
                margin-left: -4px !important;
                margin-top: -4px !important;
                border-radius: 50% !important;
                background: #fff !important;
                box-shadow: 0 0 0 2px rgba(0,0,0,0.4) inset !important;
            }

            /* Prevent Clear Polygon button from changing size on hover/focus */
            /* Stronger rules to prevent any hover/active/focus visual changes that affect size */
            #clearPolygonBtn,
            #clearPolygonBtn:hover,
            #clearPolygonBtn:active,
            #clearPolygonBtn:focus {
                transform: none !important;
                transition: none !important;
                box-shadow: none !important;
                outline: none !important;
                padding: 0.5rem 0.75rem !important;
                min-height: 36px !important;
                line-height: 1 !important;
                box-sizing: border-box !important;
            }

            /* Ensure the label inside the PrimeReact button doesn't change layout */
            #clearPolygonBtn .p-button-label {
                display: inline-block !important;
                vertical-align: middle !important;
            }
        `;
        document.head.appendChild(styleEl);

        // handle created (when user finishes drawing)
        map.on(L.Draw.Event.CREATED, function (e) {
            const layer = e.layer;
            // clear previous drawings
            drawnItems.clearLayers();
            drawnItems.addLayer(layer);
            const geojson = layer.toGeoJSON();
            setPolygonGeoJSON(geojson);
            // if a programmatic drawer is active, disable it
            if (drawerRef.current && drawerRef.current.disable) {
                try {
                    drawerRef.current.disable();
                } catch (err) {
                    /* ignore */
                }
            }
            setIsDrawing(false);
            isDrawingRef.current = false;
            // restore marker to device location if available
            if (devicePositionRef.current) {
                addOrMoveMarker(devicePositionRef.current);
            }
        });

        // handle edits and deletions to keep geojson in sync
        map.on(L.Draw.Event.EDITED, function (e) {
            const layers = e.layers;
            layers.eachLayer(function (layer) {
                setPolygonGeoJSON(layer.toGeoJSON());
            });
        });
        map.on(L.Draw.Event.DELETED, function () {
            setPolygonGeoJSON(null);
            // re-enable draw control when polygon is deleted via draw toolbar
            try {
                const drawBtn = document.querySelector('.draw-polygon-btn');
                if (drawBtn) {
                    drawBtn.removeAttribute('disabled');
                    drawBtn.style.opacity = '';
                }
            } catch (err) {}
        });

        // Default behaviour: do not auto-center to device on mount. Use Locate control to center to device when needed.
        return () => {
            map.off('click', onMapClick);
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
            try {
                document.head.removeChild(styleEl);
            } catch (e) {}
        };
    }, []);

    const handleLocateClick = () => {
        if (!mapRef.current) {
            alert('Map is not ready yet.');
            return;
        }
        if (!navigator.geolocation) {
            alert('Geolocation not supported by this browser.');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                const latlng = L.latLng(lat, lng);
                devicePositionRef.current = latlng;
                mapRef.current.setView(latlng, 15);
                addOrMoveMarker(latlng);
            },
            (err) => {
                toast.warn('Unable to get your location: ' + err.message);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleToggleDraw = () => {
        if (!mapRef.current) {
            alert('Map is not ready yet.');
            return;
        }

        // If a polygon already exists, clear it so user can redraw (edit case)
        if (polygonGeoJSON && drawnItemsRef.current) {
            try {
                drawnItemsRef.current.clearLayers();
                setPolygonGeoJSON(null);
            } catch (err) {
                toast.warn('Failed to clear existing boundary. Please try again.');
            }
        }

        // if already drawing, disable
        if (isDrawing) {
            if (drawerRef.current && drawerRef.current.disable) {
                try {
                    drawerRef.current.disable();
                } catch (err) {
                    /* ignore */
                }
            }
            setIsDrawing(false);
            isDrawingRef.current = false;
            return;
        }

        // create a polygon drawer and enable it
        const options = {
            showArea: true,
            allowIntersection: false,
            drawError: { color: '#e1e100', message: "Polygon can't intersect itself" },
            shapeOptions: { color: '#bada55' }
        };
        const drawer = new L.Draw.Polygon(mapRef.current, options);
        drawerRef.current = drawer;
        drawer.enable();
        setIsDrawing(true);
        isDrawingRef.current = true;
    };

    const handleClearPolygon = () => {
        if (drawnItemsRef.current) {
            drawnItemsRef.current.clearLayers();
        }
        setPolygonGeoJSON(null);
        // allow drawing again
        setIsDrawing(false);
        isDrawingRef.current = false;
        // re-enable the in-map draw button if present
        try {
            const drawBtn = document.querySelector('.draw-polygon-btn');
            if (drawBtn) {
                drawBtn.removeAttribute('disabled');
                drawBtn.style.opacity = '';
                drawBtn.classList.remove('active');
            }
        } catch (err) {}
    };

    // File selection / drop handlers for modern upload UI
    const handleFileSelected = (file) => {
        if (!file) return;
        try {
            // revoke previous object URL if we created one
            if (zoneImagePreview && zoneImageFile) {
                try {
                    URL.revokeObjectURL(zoneImagePreview);
                } catch (e) {}
            }
        } catch (e) {}
        setZoneImageFile(file);
        try {
            const url = URL.createObjectURL(file);
            setZoneImagePreview(url);
            // Update formik field to indicate image is present
            formik.setFieldValue('zoneImage', url);
        } catch (err) {
            setZoneImagePreview('');
        }
        // prefer file state over existing URL
        setZoneImage('');
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0] ? e.dataTransfer.files[0] : null;
        if (f) handleFileSelected(f);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const formik = useFormik({
        initialValues: {
            zoneName: '',
            zoneCode: '',
            description: '',
            address: '',
            city: '',
            // sector: '',
            // radiusMeters: '',
            totalCapacity: 10,
            zoneImage: '',
            peakHours: [
                {
                    startTime: '',
                    endTime: '',
                    multiplier: ''
                }
            ],
            vehicleRates: [
                {
                    vehicleTypeID: '',
                    ratePerHour: ''
                }
            ]
        },
        validate: (data) => {
            let errors = {};
            if (!data.zoneName) errors.zoneName = 'Zone Name is required.';
            if (!data.zoneCode) errors.zoneCode = 'Zone Code is required.';
            // Ensure vehicle rates are provided for all vehicle types (mandatory)
            try {
                if (vehicleTypes && vehicleTypes.length > 0) {
                    const missing = vehicleTypes.some((vt) => {
                        const found = (vehicleRates || []).find((r) => String(r.vehicleTypeID) === String(vt.id));
                        return !found || found.ratePerHour === '' || found.ratePerHour == null || isNaN(Number(found.ratePerHour));
                    });
                    if (missing) {
                        errors.vehicleRates = 'Please provide rates for all vehicle types.';
                    }
                }
            } catch (err) {
                // ignore validation errors
            }
            // if (!data.description) errors.description = 'Description is required.';
            // if (!data.address) errors.address = 'Address is required.';
            // if (!data.sector) errors.sector = 'Sector is required.';
            // if (!data.radiusMeters) errors.radiusMeters = 'Radius is required.';
            // if (!data.totalCapacity) errors.totalCapacity = 'Total Capacity is required.';
            // if (!initialData && !data.zoneImage) errors.zoneImage = 'Zone Image is required.';
            return errors;
        },
        onSubmit: async (data) => {
            // If vehicle types exist and rates are missing, open rates dialog and stop submission
            if (vehicleTypes && vehicleTypes.length > 0) {
                const missing = vehicleTypes.some((vt) => {
                    const found = (vehicleRates || []).find((r) => String(r.vehicleTypeID) === String(vt.id));
                    return !found || found.ratePerHour === '' || found.ratePerHour == null || isNaN(Number(found.ratePerHour));
                });
                if (missing) {
                    setShowRatesDialog(true);
                    toast.warn('Please add vehicle rates for all vehicle types before saving.');
                    return;
                }
            }
            if (!polygonGeoJSON) {
                toast.warn('Please draw a polygon for the zone boundary.');
                return;
            }
            if (submitting) return;
            setSubmitting(true);
            // If a file was selected, convert to base64
            let imageData = zoneImage || null;
            if (zoneImageFile) {
                try {
                    imageData = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = (err) => reject(err);
                        reader.readAsDataURL(zoneImageFile);
                    });
                } catch (err) {
                    toast.warn('Failed to process the selected image. Please try again.');
                }
            } else if (zoneImagePreview) {
                imageData = zoneImagePreview;
            }

            // Clean base64 data by removing data URL prefix
            let cleanImageData = imageData;
            if (imageData && imageData.startsWith('data:image/')) {
                const base64Index = imageData.indexOf('base64,');
                if (base64Index !== -1) {
                    cleanImageData = imageData.substring(base64Index + 7); // +7 to skip "base64,"
                }
            }

            // For update case, if no new image selected, set to empty string
            if (initialData && !zoneImageFile) {
                cleanImageData = '';
            }

            const payload = {
                ...(initialData ? { zoneID: initialData.zone.id } : {}),
                userId: userId || 0,
                zoneCode: data.zoneCode || '',
                zoneName: data.zoneName || '',
                description: data.description || '',
                address: data.address || '',
                city: 'Islamabad',
                totalCapacity: 10,
                // sector: data.sector || '',
                boundaryGeoJSON: polygonGeoJSON ? JSON.stringify(polygonGeoJSON) : null,
                // include the first point of polygon as latitude/longitude in payload
                latitude: (() => {
                    try {
                        if (!polygonGeoJSON) return 0;
                        const coords = polygonGeoJSON.geometry && polygonGeoJSON.geometry.coordinates ? polygonGeoJSON.geometry.coordinates : null;
                        if (!coords || !Array.isArray(coords) || coords.length === 0) return 0;
                        const firstRing = coords[0];
                        if (!Array.isArray(firstRing) || firstRing.length === 0) return 0;
                        const firstPoint = firstRing[0];
                        if (!Array.isArray(firstPoint) || firstPoint.length < 2) return 0;
                        // GeoJSON coords are [lng, lat]
                        const lat = Number(firstPoint[1]);
                        return isNaN(lat) ? 0 : lat;
                    } catch (err) {
                        return 0;
                    }
                })(),
                longitude: (() => {
                    try {
                        if (!polygonGeoJSON) return 0;
                        const coords = polygonGeoJSON.geometry && polygonGeoJSON.geometry.coordinates ? polygonGeoJSON.geometry.coordinates : null;
                        if (!coords || !Array.isArray(coords) || coords.length === 0) return 0;
                        const firstRing = coords[0];
                        if (!Array.isArray(firstRing) || firstRing.length === 0) return 0;
                        const firstPoint = firstRing[0];
                        if (!Array.isArray(firstPoint) || firstPoint.length < 2) return 0;
                        // GeoJSON coords are [lng, lat]
                        const lng = Number(firstPoint[0]);
                        return isNaN(lng) ? 0 : lng;
                    } catch (err) {
                        return 0;
                    }
                })(),
                // radiusMeters: data.radiusMeters != null ? data.radiusMeters : 0,
                // totalCapacity: data.totalCapacity != null ? data.totalCapacity : 0,
                isEnforcementZone: true,
                requiresPayment: true,
                appliedMultiplier: 1,
                // requiresPayment: !!requiresPayment,
                ImageBase64: cleanImageData,
                peakHours: (peakHours || [])
                    .filter((ph) => ph.startTime && ph.endTime)
                    .map((ph) => ({
                        startTime: ph.startTime.length === 5 ? `${ph.startTime}:00` : ph.startTime,
                        endTime: ph.endTime.length === 5 ? `${ph.endTime}:00` : ph.endTime,
                        multiplier: `${ph.multiplier}`
                    })),
                vehicleRates:
                    vehicleTypes && vehicleTypes.length > 0
                        ? vehicleTypes.map((vt) => {
                              const found = (vehicleRates || []).find((r) => String(r.vehicleTypeID) === String(vt.id));
                              return { vehicleTypeID: vt.id, ratePerHour: found ? found.ratePerHour : 0 };
                          })
                        : vehicleRates || []
            };

            try {
                const response = await dispatch(handlePostRequest(payload, initialData ? 'Zone/UpdateZone' : 'Zone/AddZoneP', true, true));
                if (response?.success) {
                    if (onClose && typeof onClose === 'function') {
                        // pass `true` to indicate parent should refresh its list
                        try {
                            onClose(true);
                        } catch (err) {
                            onClose();
                        }
                    }
                }
            } catch (err) {
                toast.error('Failed to save zone. Please try again.');
            } finally {
                setSubmitting(false);
            }
        }
    });

    // When user attempts to submit and vehicleRates validation fails,
    // open the rates dialog and show the error message under the rates section.
    useEffect(() => {
        try {
            if (formik.submitCount > 0 && formik.errors && formik.errors.vehicleRates) {
                setVehicleRatesError(formik.errors.vehicleRates);
                setShowRatesDialog(true);
            } else {
                setVehicleRatesError('');
            }
        } catch (err) {
            setVehicleRatesError('');
        }
    }, [formik.submitCount, formik.errors && formik.errors.vehicleRates]);

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    return (
        <div className="grid">
            <div className="col-12">
                <div className="Card__Round" style={{ border: 'none', height: '100%', overflow: 'hidden' }}>
                    <div className="flex justify-between items-center w-full"></div>
                    <div
                        style={{
                            height: '1px',
                            background: '#e0e0e0',
                            margin: '1rem 0',
                            borderRadius: 2
                        }}
                        role="separator"
                        aria-hidden="true"
                    />
                    <div
                        className="p-fluid p-formgrid grid mb-5 col-12 md:col-12"
                        style={{
                            height: 'calc(100% - 2rem)',
                            display: 'flex',
                            gap: '0.75rem',
                            alignItems: 'flex-start'
                        }}
                    >
                        <div className="p-fluid p-formgrid grid col-12 md:col-7" style={{ flex: '0 0 60%', maxHeight: 'calc(100vh - 160px)' }}>
                            <div style={{ flex: 2, minHeight: '420px', position: 'sticky', top: 20, alignSelf: 'flex-start' }}>
                                <div className="mb-3 flex gap-2 align-items-center justify-content-end">
                                    <Button
                                        label={isDrawing ? 'Cancel Drawing' : polygonGeoJSON ? 'Redraw Polygon' : 'Draw Polygon'}
                                        className="p-button-primary ml-auto Btn__Dark"
                                        onClick={handleToggleDraw}
                                        style={{ width: '18%', borderRadius: '10px' }}
                                    />
                                    <Button
                                        label="Clear Polygon"
                                        className="p-button-secondary"
                                        onClick={handleClearPolygon}
                                        style={{ width: '18%', borderRadius: '10px', backgroundColor: 'rgba(68, 72, 109, 0.07)', borderColor: 'rgba(68, 72, 109, 0.07)', color: 'black' }}
                                    />
                                </div>
                                <div ref={mapElRef} style={{ height: '100%', minHeight: '70vh', width: '100%', borderRadius: 4, overflow: 'hidden' }} />
                            </div>
                        </div>
                        <div
                            className="mt-2 mb-2 ml-1"
                            style={{
                                width: '1px',
                                background: '#e0e0e0',
                                alignSelf: 'stretch',
                                borderRadius: 2
                            }}
                            aria-hidden="true"
                        />
                        <div className="p-fluid p-formgrid grid col-12 md:col-5" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 160px)', flex: '1 1 40%', paddingRight: 8 }}>
                            <div className="p-fluid p-formgrid grid mb-5 col-12 md:col-12">
                                <div className="p-fluid p-formgrid grid col-12 md:col-6">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="zoneName" className={classNames({ 'p-erro': isFormFieldValid('zoneName') }, 'Label__Text')}>
                                                Zone Name <span className="Label__Required">*</span>
                                            </label>
                                            <InputText id="zoneName" name="zoneName" value={formik?.values?.zoneName || ''} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('zoneName') }, 'Input__Round mt-1')} />
                                            {getFormErrorMessage('zoneName')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-6">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="zoneCode" className={classNames({ 'p-erro': isFormFieldValid('zoneCode') }, 'Label__Text')}>
                                                Zone Code <span className="Label__Required">*</span>
                                            </label>
                                            <InputText id="zoneCode" name="zoneCode" value={formik?.values?.zoneCode || ''} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('zoneCode') }, 'Input__Round mt-1')} />
                                            {getFormErrorMessage('zoneCode')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-12">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="description" className={classNames({ 'p-erro': isFormFieldValid('description') }, 'Label__Text')}>
                                                Description
                                            </label>
                                            <InputText
                                                id="description"
                                                name="description"
                                                value={formik?.values?.description || ''}
                                                onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('description') }, 'Input__Round mt-1')}
                                            />
                                            {getFormErrorMessage('description')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-12">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="address" className={classNames({ 'p-erro': isFormFieldValid('address') }, 'Label__Text')}>
                                                Address
                                            </label>
                                            <InputText id="address" name="address" value={formik?.values?.address || ''} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('address') }, 'Input__Round mt-1')} />
                                            {getFormErrorMessage('address')}
                                        </div>
                                    </div>
                                </div>
                                {/* <div className="p-fluid p-formgrid grid col-12 md:col-6">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="sector" className={classNames({ 'p-erro': isFormFieldValid('sector') }, 'Label__Text')}>
                                                Sector <span className="Label__Required">*</span>
                                            </label>
                                            <InputText id="sector" name="sector" value={formik?.values?.sector || ''} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('sector') }, 'Input__Round mt-1')} />
                                            {getFormErrorMessage('sector')}
                                        </div>
                                    </div>
                                </div> */}
                                {/* <div className="p-fluid p-formgrid grid col-12 md:col-6">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="radius" className={classNames({ 'p-erro': isFormFieldValid('radiusMeters') }, 'Label__Text')}>
                                                Radius <span className="Label__Required">*</span>
                                            </label>
                                            <InputText
                                                id="radius"
                                                name="radiusMeters"
                                                value={formik?.values?.radiusMeters || ''}
                                                onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('radiusMeters') }, 'Input__Round mt-1')}
                                                keyfilter="int"
                                            />
                                            {getFormErrorMessage('radiusMeters')}
                                        </div>
                                    </div>
                                </div> */}
                                {/* <div className="p-fluid p-formgrid grid col-12 md:col-6">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="totalCapacity" className={classNames({ 'p-erro': isFormFieldValid('totalCapacity') }, 'Label__Text')}>
                                                Total Capacity <span className="Label__Required">*</span>
                                            </label>
                                            <InputText
                                                id="totalCapacity"
                                                name="totalCapacity"
                                                value={formik?.values?.totalCapacity || ''}
                                                onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('totalCapacity') }, 'Input__Round mt-1')}
                                                keyfilter="int"
                                            />
                                            {getFormErrorMessage('totalCapacity')}
                                        </div>
                                    </div>
                                </div> */}
                                <div className="p-fluid p-formgrid grid col-12 md:col-12">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="zoneImage" className={classNames({ 'p-erro': isFormFieldValid('zoneImage') }, 'Label__Text')}>
                                                Zone Image
                                            </label>
                                            {/* <InputText
                                                type="file"
                                                id="zoneImage"
                                                name="zoneImage"
                                                value={formik?.values?.zoneImage || ''}
                                                onChange={formik.handleChange}
                                                className={classNames({ 'p-invalid': isFormFieldValid('zoneImage') }, 'Input__Round mt-1')}
                                            /> */}
                                            <div
                                                onDrop={handleDrop}
                                                onDragOver={handleDragOver}
                                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                                role="button"
                                                tabIndex={0}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        fileInputRef.current && fileInputRef.current.click();
                                                    }
                                                }}
                                                style={{
                                                    marginTop: 8,
                                                    border: '2px dashed #d9d9d9',
                                                    borderRadius: 8,
                                                    padding: 12,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12,
                                                    background: '#fafafa'
                                                }}
                                            >
                                                {' '}
                                                <input
                                                    id="zoneImageFile"
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                                                        handleFileSelected(file);
                                                    }}
                                                    style={{ display: 'none' }}
                                                />
                                                {/* Preview or placeholder */}
                                                {zoneImagePreview || zoneImage ? (
                                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                                            <img
                                                                src={zoneImagePreview || zoneImage}
                                                                alt="Zone preview"
                                                                style={{ width: 160, height: 100, objectFit: 'cover', borderRadius: 8, flex: '0 0 auto', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                                                            />
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                                <div style={{ fontSize: '0.95rem', color: '#333', fontWeight: 600 }}>{zoneImageFile ? zoneImageFile.name : 'Selected image'}</div>
                                                                <div style={{ fontSize: '0.85rem', color: '#666' }}>Click to Replace or drag a new image here.</div>
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <Button label="Replace" onClick={() => fileInputRef.current && fileInputRef.current.click()} className="p-button-outlined" style={{ height: 36 }} />
                                                            <Button
                                                                label="Remove"
                                                                className="p-button-secondary"
                                                                onClick={() => {
                                                                    try {
                                                                        if (zoneImageFile && zoneImagePreview) URL.revokeObjectURL(zoneImagePreview);
                                                                    } catch (e) {}
                                                                    setZoneImageFile(null);
                                                                    setZoneImagePreview('');
                                                                    setZoneImage('');
                                                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                                                }}
                                                                style={{ height: 36 }}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                                                        <div style={{ flex: '0 0 auto', width: 80, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, background: '#fff' }}>
                                                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M12 16V8" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                <path d="M8 12l4-4 4 4" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                <rect x="3" y="3" width="18" height="18" rx="3" stroke="#e6e6e6" strokeWidth="1.2" />
                                                            </svg>
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontSize: '0.95rem', color: '#333', fontWeight: 600 }}>Drag & drop an image, or click to select</div>
                                                            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: 6 }}>PNG, JPG, JPEG — Recommended 1600x900 or similar landscape ratio.</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {getFormErrorMessage('zoneImage')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-12">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <Button
                                                className="Btn__Dark"
                                                icon={showRatesDialog ? 'pi pi-angle-up' : 'pi pi-angle-down'}
                                                label={showRatesDialog ? 'Close Vehicle Rates' : 'Add Vehicle Rates'}
                                                onClick={openRatesDialog}
                                                aria-expanded={showRatesDialog}
                                                type="button"
                                            />
                                        </div>
                                    </div>
                                </div>
                                {showRatesDialog && (
                                    <>
                                        <div className="p-fluid p-formgrid grid col-12 md:col-12 p-3" style={{ border: '1px solid #e6e6e6', borderRadius: 14, marginTop: 8 }}>
                                            <div className="p-field col-12 md:col-12 pt-3">
                                                <div className="p-field">
                                                    <div className="flex justify-content-between font-bold align-items-center">
                                                        <h5 className="Label__Text m-0">Vehicle Types</h5>
                                                        <h5 className="mr-8 Label__Text m-0">Rates</h5>
                                                    </div>
                                                    <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                                                    {(vehicleTypes || []).map((vt) => (
                                                        <div key={vt.id} style={{ display: 'flex', gap: 12, alignItems: 'center', borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                                                            <div style={{ flex: 1 }}>{vt.name}</div>
                                                            <div style={{ width: 160 }}>
                                                                <InputText
                                                                    className="Input__Round mt-1"
                                                                    type="text"
                                                                    inputMode="numeric"
                                                                    pattern="[0-9]*"
                                                                    value={vehicleRates.find((r) => String(r.vehicleTypeID) === String(vt.id))?.ratePerHour || ''}
                                                                    onChange={(e) => handleTempRateChange(vt.id, e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        {vehicleRatesError && (
                                            <div className="p-fluid p-formgrid grid col-12 md:col-12" style={{ marginTop: 8 }}>
                                                <div style={{ color: '#d9534f', textAlign: 'left', paddingLeft: 8 }}>{vehicleRatesError}</div>
                                            </div>
                                        )}
                                        <div className="p-fluid p-formgrid grid col-12 md:col-12 mt-4">
                                            <div className="flex justify-content-center w-full">
                                                <Button label="Cancel" className="p-button-secondary Black__Btn" onClick={() => setShowRatesDialog(false)} />
                                                <Button label="Save Rates" className="p-button-primary Btn__Dark" onClick={saveRatesFromDialog} />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* <div className="p-fluid p-formgrid grid col-12 md:col-12">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <Button
                                                className="Btn__Dark"
                                                icon={showPeakHoursDialog ? 'pi pi-angle-up' : 'pi pi-angle-down'}
                                                label={showPeakHoursDialog ? 'Close Peak Hours' : 'Add Peak Hours'}
                                                onClick={openPeakHoursDialog}
                                                aria-expanded={showPeakHoursDialog}
                                                type="button"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-12">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            {showPeakHoursDialog && (
                                                <div style={{ border: '1px solid #e6e6e6', padding: 12, borderRadius: 14, marginTop: 8 }}>
                                                    {peakHours.map((ph, idx) => (
                                                        <div key={idx} style={{ border: '1px solid #eee', padding: 8, marginTop: 8, borderRadius: 6 }}>
                                                            <div className="p-grid">
                                                                <div className="p-col-4">
                                                                    <label>Start Time</label>
                                                                    <input
                                                                        type="time"
                                                                        value={ph.startTime || ''}
                                                                        className="Input__Round mt-1"
                                                                        onChange={(e) => {
                                                                            const copy = [...peakHours];
                                                                            copy[idx] = { ...copy[idx], startTime: e.target.value };
                                                                            setPeakHours(copy);
                                                                        }}
                                                                        style={{ width: '100%', padding: '0.4rem' }}
                                                                    />
                                                                </div>
                                                                <div className="p-col-4">
                                                                    <label>End Time</label>
                                                                    <input
                                                                        type="time"
                                                                        value={ph.endTime || ''}
                                                                        className="Input__Round mt-1"
                                                                        onChange={(e) => {
                                                                            const copy = [...peakHours];
                                                                            copy[idx] = { ...copy[idx], endTime: e.target.value };
                                                                            setPeakHours(copy);
                                                                        }}
                                                                        style={{ width: '100%', padding: '0.4rem' }}
                                                                    />
                                                                </div>
                                                                <div className="p-col-4">
                                                                    <label>Multiplier</label>
                                                                    <select
                                                                        value={ph.multiplier ?? 1}
                                                                        className="Input__Round mt-1"
                                                                        onChange={(e) => {
                                                                            const val = parseInt(e.target.value || 0, 10);
                                                                            const copy = [...peakHours];
                                                                            copy[idx] = { ...copy[idx], multiplier: val };
                                                                            setPeakHours(copy);
                                                                        }}
                                                                        style={{ width: '100%', padding: '0.4rem' }}
                                                                    >
                                                                        {Array.from({ length: 5 }, (_, i) => i + 1).map((n) => (
                                                                            <option key={n} value={n}>
                                                                                {n}x
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div className="p-col-12 mt-2" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                                                    {(() => {
                                                                        const plusDisabled = peakHours.length >= 5;
                                                                        const plusStyle = plusDisabled
                                                                            ? { background: '#f2fbf4', border: '1px solid #50C878', color: '#2f7a45', width: '50px', cursor: 'not-allowed' }
                                                                            : { background: '#50C878', border: 'none', color: '#fff', width: '50px' };
                                                                        return (
                                                                            <Button
                                                                                label="+"
                                                                                className="Btn__Dark"
                                                                                style={plusStyle}
                                                                                onClick={() => {
                                                                                    if (peakHours.length < 5) {
                                                                                        const copy = [...peakHours];
                                                                                        copy.splice(idx + 1, 0, { startTime: '', endTime: '', multiplier: 1 });
                                                                                        setPeakHours(copy);
                                                                                    }
                                                                                }}
                                                                                disabled={plusDisabled}
                                                                            />
                                                                        );
                                                                    })()}

                                                                    {(() => {
                                                                        const minusDisabled = peakHours.length <= 1;
                                                                        const minusStyle = minusDisabled
                                                                            ? { background: '#fff5f5', border: '1px solid #D22B2B', color: '#8a1f1f', width: '50px', cursor: 'not-allowed' }
                                                                            : { background: '#D22B2B', border: 'none', color: '#fff', width: '50px' };
                                                                        return (
                                                                            <Button
                                                                                label="-"
                                                                                className="Btn__Dark"
                                                                                style={minusStyle}
                                                                                onClick={() => {
                                                                                    const copy = [...peakHours];
                                                                                    copy.splice(idx, 1);
                                                                                    setPeakHours(copy);
                                                                                }}
                                                                                disabled={minusDisabled}
                                                                            />
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div> */}
                                <div className="p-fluid p-formgrid grid col-12 md:col-12">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button
                                                    type="submit"
                                                    label={submitting ? 'Saving...' : 'Save Zone'}
                                                    className="Btn__Dark"
                                                    onClick={formik.handleSubmit}
                                                    disabled={submitting}
                                                    icon={submitting ? 'pi pi-spin pi-spinner' : undefined}
                                                    style={{ borderRadius: '8px', padding: '0.6rem 1rem' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddNewZone;
